/* eslint-disable i18next/no-literal-string */
import React, { useEffect, useState } from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useFormContext } from 'react-hook-form';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import { ApproverType, IApproverType } from './Controls/ApproverType';
import { FormInputText } from 'app/components/Candidate/CandidateProfile/CandidateTabPanel/PersonalInfoTab/Form/Controls/FormInputText';
import { FormInputDatePicker } from 'app/components/Candidate/CandidateProfile/CandidateTabPanel/PersonalInfoTab/Form/Controls/FormInputDatePicker';
import { ExceptionWaiveAttachmentWrapper } from '../ExceptionWaiveAttachmentWrapper';
import { ExceptionWaiveSelection } from '../../../../Store/PlacementEventDetails.selector';
import { useSelector } from 'react-redux';

const useStyles = makeStyles()((theme: Theme) => ({
  datePickerInput: {
    height: '55px',
    fontSize: '14px',
  },
  spacing: {
    margin: '10px 0px',
  },
  header: {
    fontSize: '12px',
    color: '#555555',
    paddingBottom: '10px',
    fontWeight: 500,
  },
  label: {
    fontSize: '14px',
  },
}));

export const ExceptionDetails = ({
  isChanged,
  selectedStatusType,
}: {
  isChanged: (name, value) => void;
  selectedStatusType: any;
}) => {
  const { control, formState: { errors }, getValues, setValue, setError, clearErrors } = useFormContext();
  const { classes } = useStyles();
  const exceptionWaiveSel = useSelector(ExceptionWaiveSelection);
  const [isClient, setIsClient] = useState<boolean>(true);
  const [isException, setIsException] = useState<boolean>(true);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [selectedApproverType, setSelectedApproverType] = useState(IApproverType.amn);

  useEffect(() => {
    if (exceptionWaiveSel) {
      setValue('amnApproverName', exceptionWaiveSel.amnApproverName);
      setValue('amnApproverTitle', exceptionWaiveSel.amnApproverTitle);
      setValue('expirationDate', exceptionWaiveSel.expirationDate);
      setValue('clientApproverName', exceptionWaiveSel.clientApproverName);
      setValue('clientApproverTitle', exceptionWaiveSel.clientApproverTitle);
      setValue('clientDepartment', exceptionWaiveSel.clientDepartment);
      setValue('notes', exceptionWaiveSel.notes);
      setValue('approverType', exceptionWaiveSel.clientApproverName ? IApproverType.client : IApproverType.amn);
      setExpirationDate(exceptionWaiveSel?.expirationDate ? exceptionWaiveSel.expirationDate : null);
      setIsException(exceptionWaiveSel.type === 'Exception');
      setIsClient(
        (exceptionWaiveSel.clientApproverName ? IApproverType.client : IApproverType.amn) === IApproverType.client,
      );
    } else {
      setIsClient(getValues('approverType') === IApproverType.client);
      setIsException(getValues('type') === 'Exception');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsClient(getValues('approverType') === IApproverType.client);
    setIsException(getValues('type') === 'Exception');
  }, [selectedStatusType, selectedApproverType]);

  useEffect(() => {
    if (expirationDate) {
      setValue('expirationDate', expirationDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isException]);

  const onChangeDate = e => {
    isChanged('expirationDate', e);
    setValue('expirationDate', e, { shouldDirty: true });
    setExpirationDate(e);
    if (!getValues('expirationDate')) {
      setError('expirationDate', {
        type: 'required',
        message: 'Required',
      });
    } else {
      clearErrors(['expirationDate']);
    }
  };

  return (
    <LayoutGrid direction="column">
      <LayoutGridItem xs={12} className={classes.spacing}>
        {/* Radio Button */}
        <ApproverType
          control={control}
          customClasses={classes.header}
          setSelectedApproverType={setSelectedApproverType}
        />
      </LayoutGridItem>
      <LayoutGridItem xs={12} className={classes.spacing}>
        {/* Expiration Date / Names */}
        <LayoutGrid>
          {isException && (
            <LayoutGridItem style={{ width: '25%' }}>
              <FormInputDatePicker
                name={'expirationDate'}
                label={'Expiration Date*'}
                handleChange={onChangeDate}
                customClass={classes.datePickerInput}
                trailingIconVisible
                labelClass={classes.label}
                inputValue={getValues('expirationDate') || expirationDate}
                keepClickAwayVal
              ></FormInputDatePicker>
            </LayoutGridItem>
          )}
          {isClient && (
            <React.Fragment>
              <LayoutGridItem style={{ width: '37.5%' }}>
                <FormInputText
                  name="clientApproverName"
                  label={'Client Approver Name'}
                  disableInputRef
                  require={false}
                  handleChange={e => {
                    isChanged('clientApproverName', e.target.value);
                    setValue('clientApproverName', e.target.value, { shouldDirty: true });
                  }}
                  inputValue={exceptionWaiveSel?.clientApproverName ? exceptionWaiveSel.clientApproverName : undefined}
                  message={errors['clientApproverName']?.message || ''}
                />
              </LayoutGridItem>
              <LayoutGridItem style={{ width: '37.5%' }}>
                <FormInputText
                  name="clientApproverTitle"
                  label={'Client Approver Title'}
                  disableInputRef
                  require={false}
                  handleChange={e => {
                    isChanged('clientApproverTitle', e.target.value);
                    setValue('clientApproverTitle', e.target.value, { shouldDirty: true });
                  }}
                  inputValue={
                    exceptionWaiveSel?.clientApproverTitle ? exceptionWaiveSel.clientApproverTitle : undefined
                  }
                  message={errors['clientApproverTitle']?.message || ''}
                />
              </LayoutGridItem>
              <LayoutGridItem style={{ width: '25%' }}>
                <FormInputText
                  name="clientDepartment"
                  label={'Client Department'}
                  disableInputRef
                  require={false}
                  handleChange={e => {
                    isChanged('clientDepartment', e.target.value);
                    setValue('clientDepartment', e.target.value, { shouldDirty: true });
                  }}
                  inputValue={exceptionWaiveSel?.clientDepartment ? exceptionWaiveSel.clientDepartment : undefined}
                  message={errors['clientDepartment']?.message || ''}
                />
              </LayoutGridItem>
            </React.Fragment>
          )}
          <LayoutGridItem style={{ width: '37.5%' }}>
            <FormInputText
              name="amnApproverName"
              label={'AMN Approver Name'}
              disableInputRef
              require={false}
              handleChange={e => {
                isChanged('amnApproverName', e.target.value);
                setValue('amnApproverName', e.target.value, { shouldDirty: true });
              }}
              inputValue={exceptionWaiveSel?.amnApproverName ? exceptionWaiveSel.amnApproverName : undefined}
              message={errors['amnApproverName']?.message || ''}
            />
          </LayoutGridItem>
          <LayoutGridItem style={{ width: '37.5%' }}>
            <FormInputText
              name="amnApproverTitle"
              label={'AMN Approver Title'}
              disableInputRef
              require={false}
              handleChange={e => {
                isChanged('amnApproverTitle', e.target.value);
                setValue('amnApproverTitle', e.target.value, { shouldDirty: true });
              }}
              inputValue={exceptionWaiveSel?.amnApproverTitle ? exceptionWaiveSel.amnApproverTitle : undefined}
              message={errors['amnApproverTitle']?.message || ''}
            />
          </LayoutGridItem>
        </LayoutGrid>
      </LayoutGridItem>
      {/* TODO: Add Blame.tsx component here for ADD REQUIREMENT */}
      {/* <LayoutGridItem xs={12}>
        <Blame type={type} />
      </LayoutGridItem> */}
      <LayoutGridItem xs={12} className={classes.spacing}>
        <FormInputText
          name="notes"
          label={'Notes'}
          disableInputRef
          require={false}
          handleChange={e => {
            isChanged('notes', e.target.value);
            setValue('notes', e.target.value, { shouldDirty: true });
          }}
          inputValue={exceptionWaiveSel?.notes ? exceptionWaiveSel.notes : undefined}
          message={errors['notes']?.message || ''}
        />
        {/* <Notes control={control} /> */}
      </LayoutGridItem>
      <LayoutGridItem xs={12}>
        <ExceptionWaiveAttachmentWrapper isChanged={isChanged} />
      </LayoutGridItem>
    </LayoutGrid>
  );
};
